import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {apiClient} from '../restClient'
import {useAppStore} from '../store/appStore'
import {PerPageOneHundred} from '../constants/perPageConstants'
import {wt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../Translation/English/global'
import {
  CREATE_DEFAULT_IDEA_ENDPOINT,
  CREATE_IDEA_COMMENTS_ENDPOINT,
  CREATE_IDEA_ENDPOINT,
  CREATE_IDEAS_VOTE_ENDPOINT,
  CREATE_IDEATION_ENDPOINT,
  DELETE_IDEATIONS_ENDPOINT,
  GET_DRAFT_IDEATION_ENDPOINT,
  GET_IDEATION_ENDPOINT,
  GET_IDEATION_INSIGHTS_ENDPOINT,
  GET_IDEATIONS_ENDPOINT,
  GET_PUBLISH_IDEATION_ENDPOINT,
  GET_TOP_IDEAS_IDEATION_ENDPOINT,
  UPDATE_IDEATION_ENDPOINT,
  UPDATE_IDEATION_QUESTION_ENDPOINT,
  UPDATE_PARTICIPANTS_ENDPOINT,
} from './endPoints'
import type {
  IIdea,
  IIdeation,
  IIdeationExplore,
  IIdeationInsights,
  IIdeaVoteSubmission,
} from '../types'

const fetchIdeations = (
  initID: string,
  page: number,
): Promise<{ideations: IIdeation[]; count?: number}> =>
  apiClient
    .get(GET_IDEATIONS_ENDPOINT, {
      params: {
        initiativeId: initID,
        limit: PerPageOneHundred,
        offset: (page - 1) * PerPageOneHundred,
      },
    })
    .then(r => r.data)

const fetchIdeation = (ideationId: string): Promise<IIdeation> =>
  apiClient.get(`${GET_IDEATION_ENDPOINT}/${ideationId}`).then(r => r.data)

const fetchExplore = (ideationId: string): Promise<IIdeationExplore> =>
  apiClient
    .get(`${GET_TOP_IDEAS_IDEATION_ENDPOINT}/${ideationId}/top-ideas`)
    .then(r => r.data)

const fetchPreviewIdeation = (
  ideationId: string,
  sessionUserId: string,
): Promise<IIdeation> =>
  apiClient
    .get(GET_PUBLISH_IDEATION_ENDPOINT, {
      params: {id: ideationId, sessionUserId},
    })
    .then(r => r.data)

const fetchDraftIdeation = (ideationId: string): Promise<IIdeation> =>
  apiClient
    .get(GET_DRAFT_IDEATION_ENDPOINT, {params: {id: ideationId}})
    .then(r => r.data)

const fetchIdeationInsights = (
  ideationId: string,
): Promise<IIdeationInsights> =>
  apiClient
    .get(GET_IDEATION_INSIGHTS_ENDPOINT.replace(':ideationId', ideationId))
    .then(r => r.data)

export const ideationApi = {
  useIdeations: (initID: string, page: number) =>
    useQuery({
      queryKey: ['ideations', initID, page],
      queryFn: () => fetchIdeations(initID, page),
      enabled: !!initID,
      placeholderData: keepPreviousData,
    }),

  useIdeation: (ideationId: string) =>
    useQuery({
      queryKey: ['ideation', ideationId],
      queryFn: () => fetchIdeation(ideationId),
      enabled: !!ideationId,
    }),

  useExplore: (ideationId: string) =>
    useQuery({
      queryKey: ['ideation', 'explore', ideationId],
      queryFn: () => fetchExplore(ideationId),
      enabled: !!ideationId,
    }),

  usePreviewIdeation: (ideationId: string, sessionUserId: string) =>
    useQuery({
      queryKey: ['ideation', 'preview', ideationId, sessionUserId],
      queryFn: () => fetchPreviewIdeation(ideationId, sessionUserId),
      enabled: !!ideationId && !!sessionUserId,
    }),

  useDraftIdeation: (ideationId: string) =>
    useQuery({
      queryKey: ['ideation', 'draft', ideationId],
      queryFn: () => fetchDraftIdeation(ideationId),
      enabled: !!ideationId,
    }),

  useIdeationInsights: (ideationId: string) =>
    useQuery({
      queryKey: ['ideation', 'insights', ideationId],
      queryFn: () => fetchIdeationInsights(ideationId),
      enabled: !!ideationId,
    }),

  useCreateIdeation: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {initiativeId: string}) =>
        apiClient.post(CREATE_IDEATION_ENDPOINT, payload).then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['ideations']}),
      onError: () =>
        useAppStore.getState().showError(wt(GLOBAL.somethingWentWrong)),
    })
  },

  useUpdateIdeation: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        ideationId,
        ideationData,
      }: {
        ideationId: string
        ideationData: Record<string, unknown>
        type?: string
      }) =>
        apiClient
          .patch(UPDATE_IDEATION_ENDPOINT, {id: ideationId, ...ideationData})
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        void qc.invalidateQueries({
          queryKey: ['ideation', variables.ideationId],
        })
        // Lists show name and status (the Initiative rail's Ideation tab), so
        // a rename or Draft/Publish switch must refresh them too.
        void qc.invalidateQueries({queryKey: ['ideations']})
      },
      onError: () => useAppStore.getState().showError('Update ideation failed'),
    })
  },

  useDeleteIdeation: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {ids: string[]}) =>
        apiClient
          .delete(DELETE_IDEATIONS_ENDPOINT, {data: payload})
          .then(r => r.data),
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['ideations']})
        useAppStore.getState().showSuccess('Ideation successfully deleted')
      },
      onError: () =>
        useAppStore.getState().showError(wt(GLOBAL.somethingWentWrong)),
    })
  },

  useCreateIdeationQuestion: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: Record<string, unknown>) =>
        apiClient
          .post(UPDATE_IDEATION_QUESTION_ENDPOINT, payload)
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        const ideationId = (variables as {ideationId?: string}).ideationId
        if (ideationId) {
          void qc.invalidateQueries({queryKey: ['ideation', ideationId]})
        }
      },
      onError: () => useAppStore.getState().showError('Save question failed'),
    })
  },

  useCreateIdeationIdea: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {
        ideationId: string
        ideas: IIdea[]
        deletedIdeas: string[]
      }) =>
        apiClient.post(CREATE_DEFAULT_IDEA_ENDPOINT, payload).then(r => r.data),
      onSuccess: (_data, variables) =>
        void qc.invalidateQueries({
          queryKey: ['ideation', variables.ideationId],
        }),
      onError: () => useAppStore.getState().showError('Save ideas failed'),
    })
  },

  useCreateIdea: () => {
    return useMutation({
      mutationFn: (payload: {
        ideationQuestionId: string
        text: string
        sessionUserId?: string
      }) => apiClient.post(CREATE_IDEA_ENDPOINT, payload).then(r => r.data),
      onSuccess: data => {
        const currentIdeas = useAppStore.getState().ideas
        useAppStore.getState().setIdeas([data, ...currentIdeas])
      },
      onError: () => useAppStore.getState().showError('Submit idea failed'),
    })
  },

  useVoteIdea: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {
        ideationId: string
        votes: IIdeaVoteSubmission[]
      }) =>
        apiClient.post(CREATE_IDEAS_VOTE_ENDPOINT, payload).then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['ideation']}),
    })
  },

  useCommentIdea: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: Record<string, unknown>) =>
        apiClient
          .post(CREATE_IDEA_COMMENTS_ENDPOINT, payload)
          .then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['ideation']}),
    })
  },

  useConfirmUserVote: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (action: Record<string, unknown>) =>
        apiClient.post('/ideation/confirm', action).then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['ideation']}),
    })
  },

  useAddParticipant: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {ideationId: string}) =>
        apiClient
          .patch(UPDATE_PARTICIPANTS_ENDPOINT, payload)
          .then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['ideation']}),
    })
  },
} as const
